import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Space, Tag, Card, Table, theme, Image, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Field } from '../../components/Field';
import { GpsLocation } from '../../components/GpsLocation';
import { WorkoutLogSource } from '@wellverse/shared-ts';
import type { WorkoutLogDto, WorkoutLogExerciseDto, WorkoutLogSetDto } from '@wellverse/shared-ts';
import { resolveCmsMediaUrl } from '../../utils/media-url';

const { Text } = Typography;

const sourceColors: Record<WorkoutLogSource, string> = {
  [WorkoutLogSource.MANUAL]: '#1890ff',
  [WorkoutLogSource.SCAN]: '#722ed1',
  [WorkoutLogSource.PLAN]: '#3ecf8e',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const WorkoutLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<WorkoutLogDto>({ resource: 'workout-logs' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="workout-logs">
      <div style={{ padding: '8px 0' }}>
        <Field label="User ID">{item.userId}</Field>
        <Field label="Source">
          <Tag style={tagStyle(sourceColors[item.source] || '#898989')}>{item.source}</Tag>
        </Field>
        <Field label="Scan Location">
          <GpsLocation latitude={item.latitude} longitude={item.longitude} />
        </Field>
        {item.plan && (
          <Field label="Plan">
            {item.plan.name} {item.planId && `(${item.planId})`}
          </Field>
        )}
        <Field label="Started At">{new Date(item.startedAt).toLocaleString()}</Field>
        {item.completedAt && (
          <Field label="Completed At">{new Date(item.completedAt).toLocaleString()}</Field>
        )}
        {item.duration != null && <Field label="Duration">{item.duration} min</Field>}
        <Field label="Total Sets">{item.totalSets}</Field>
        <Field label="Total Reps">{item.totalReps}</Field>
        {item.caloriesBurned != null && <Field label="Calories Burned">{item.caloriesBurned}</Field>}
        {item.notes && <Field label="Notes">{item.notes}</Field>}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>

      <h3 style={{ color: token.colorText, margin: '24px 0 16px' }}>Exercises</h3>
      <Space direction="vertical" style={{ width: '100%' }}>
        {item.exercises?.map((exercise: WorkoutLogExerciseDto) => {
          const equipment = exercise.exercise?.equipment;
          const equipmentId = equipment?.id ?? exercise.exercise?.equipmentId ?? (
            exercise.equipmentUsed && exercise.equipmentUsed.length >= 32
              ? exercise.equipmentUsed
              : undefined
          );
          const equipmentName = equipment?.name;

          return (
            <Card
              key={exercise.id}
              size="small"
              title={exercise.exerciseName}
              style={{ width: '100%' }}
              extra={
                resolveCmsMediaUrl(exercise.photoUrl) ? (
                  <Image
                    src={resolveCmsMediaUrl(exercise.photoUrl)}
                    alt=""
                    width={64}
                    height={64}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                    fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23f0f0f0' width='64' height='64'/%3E%3C/svg%3E"
                  />
                ) : null
              }
            >
              <div style={{ marginBottom: 8, color: token.colorTextSecondary }}>
                Equipment:{' '}
                {equipmentId ? (
                  <Link to={`/equipment-catalog/show/${equipmentId}`}>
                    {equipmentName || equipmentId}
                  </Link>
                ) : exercise.equipmentUsed ? (
                  <Text>{exercise.equipmentUsed}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </div>
              <Table
                dataSource={exercise.sets}
                rowKey="id"
                pagination={false}
                size="small"
              >
                <Table.Column dataIndex="setNumber" title="Set" />
                <Table.Column dataIndex="reps" title="Reps" />
                <Table.Column
                  dataIndex="weightKg"
                  title="Weight (kg)"
                  render={(v: number | undefined) => (v != null ? `${v} kg` : '—')}
                />
                <Table.Column
                  dataIndex="durationSeconds"
                  title="Duration"
                  render={(v: number | undefined) => (v != null ? `${v}s` : '—')}
                />
                <Table.Column
                  dataIndex="isCompleted"
                  title="Completed"
                  render={(v: boolean) => (
                    <Tag style={tagStyle(v ? '#3ecf8e' : '#898989')}>
                      {v ? 'Yes' : 'No'}
                    </Tag>
                  )}
                />
                {exercise.sets.some((s: WorkoutLogSetDto) => s.completedAt) && (
                  <Table.Column
                    dataIndex="completedAt"
                    title="Completed At"
                    render={(date: string | undefined) =>
                      date ? new Date(date).toLocaleString() : '—'
                    }
                  />
                )}
              </Table>
            </Card>
          );
        })}
      </Space>
    </Show>
  );
};
